/* Registro del service worker y actualización CONTROLADA POR EL USUARIO.
 *
 * REGLA. Una actualización nunca recarga una pestaña por su cuenta. Antes el SW
 * llamaba a `skipWaiting()` en su `install` y `index.js` recargaba en cada
 * `controllerchange`: tras un deploy, o cuando OTRA pestaña activaba la versión
 * nueva, la pestaña recargaba sola y el prompt sin enviar del editor se perdía.
 *
 * Ahora:
 *   · `controllerchange` solo AVISA (evento `orca-sw-controller-changed`); la
 *     pestaña sigue con su JS, y el SW conserva sus bundles (ver public/sw.js).
 *   · la versión nueva queda "en espera" y el banner la ofrece
 *     (`orca-sw-update`). El usuario decide cuándo, y `applyUpdate` recarga SOLO
 *     tras un clic suyo, esperando a que acabe cualquier subida SIN techo.
 *   · cada pestaña responde al SW qué build ejecuta (`WHICH_BUILD`): así el SW
 *     solo borra la caché de un build antiguo cuando ninguna pestaña lo usa.
 *   · antes de activar se censan las pestañas abiertas: las que ejecutan este
 *     mismo código responden por BroadcastChannel; las que no (una versión
 *     anterior, que SÍ se recarga sola al cambiar el controlador) se detectan por
 *     diferencia con el recuento de clientes del SW y se avisa de ellas.
 *
 * Todo recibe `nav`/`win` por parámetro para poder probarlo con un navegador
 * real y con dobles de node.
 */

export const EVT_UPDATE_READY = "orca-sw-update";
export const EVT_CONTROLLER_CHANGED = "orca-sw-controller-changed";
const CHANNEL = "orca-tabs-v1";

const state = { waiting: null, applying: false, appliedElsewhere: false };
export const getUpdateState = () => ({ ...state });
export function _resetUpdateState() {
  state.waiting = null; state.applying = false; state.appliedElsewhere = false;
}

function emit(win, name, detail) {
  try { win.dispatchEvent(new win.CustomEvent(name, { detail })); } catch (_) { /* sin escucha */ }
}

/** Build que ejecuta ESTA pestaña: el hash del bundle principal, tal como lo
 *  deriva el postbuild (sw.js lo usa para nombrar la caché). null si no se puede
 *  determinar (dev). */
export function currentBuildId(doc = typeof document !== "undefined" ? document : null) {
  try {
    for (const el of Array.from(doc.querySelectorAll("script[src]"))) {
      const m = /\/static\/js\/main\.([a-f0-9]+)\.js(?:$|\?)/i.exec(el.getAttribute("src") || "");
      if (m) return m[1].slice(0, 12);
    }
  } catch (_) { /* sin documento */ }
  return null;
}

/** Contesta al SW cuando pregunta qué build ejecuta esta pestaña. Sin respuesta
 *  (build desconocido) el SW no poda nada: es el lado seguro. */
export function answerBuildQueries({ nav = typeof navigator !== "undefined" ? navigator : null, doc = typeof document !== "undefined" ? document : null, getBuild = () => currentBuildId(doc) } = {}) {
  const sw = nav && nav.serviceWorker;
  if (!sw) return () => {};
  const onMessage = (e) => {
    const d = e && e.data;
    if (!d || d.type !== "WHICH_BUILD") return;
    const build = getBuild();
    const port = e.ports && e.ports[0];
    if (build && port) { try { port.postMessage({ build }); } catch (_) { /* sin canal */ } }
  };
  sw.addEventListener("message", onMessage);
  try { if (typeof sw.startMessages === "function") sw.startMessages(); } catch (_) { /* ya activo */ }
  return () => sw.removeEventListener("message", onMessage);
}

/**
 * Registra el SW y publica los eventos. Devuelve `{ ready, dispose }`; `ready`
 * resuelve con el registro (o null si no hay SW / falla).
 */
export function registerServiceWorker({ nav = navigator, win = window, doc = document, path = "/sw.js" } = {}) {
  if (!nav || !nav.serviceWorker) return { ready: Promise.resolve(null), dispose() {} };
  const sw = nav.serviceWorker;
  const cleanups = [];
  // El guard es DINÁMICO: arranca del estado real y se vuelve true tras la
  // primera toma de control. Un boolean congelado al cargar haría que una
  // pestaña que vio la PRIMERA instalación ignorara los cambios posteriores.
  let hadController = !!sw.controller;

  const onControllerChange = () => {
    if (!hadController) { hadController = true; return; }   // 1ª instalación (clients.claim)
    state.waiting = null;
    state.appliedElsewhere = !state.applying;
    emit(win, EVT_CONTROLLER_CHANGED, { requested: state.applying });
  };
  sw.addEventListener("controllerchange", onControllerChange);
  cleanups.push(() => sw.removeEventListener("controllerchange", onControllerChange));
  cleanups.push(answerBuildQueries({ nav, doc }));

  const notify = (worker) => {
    // Solo es "actualización" si ya había un SW controlando; la primerísima
    // instalación no ofrece nada.
    if (!worker || !sw.controller) return;
    state.waiting = worker;
    emit(win, EVT_UPDATE_READY, { worker });
  };

  const start = () => sw.register(path).then((reg) => {
    if (reg.waiting) notify(reg.waiting);
    const onFound = () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        if (nw.state === "installed") notify(reg.waiting || nw);
      });
    };
    reg.addEventListener("updatefound", onFound);
    // Un tab abierto desde antes del deploy se entera al volver a él.
    const onVisible = () => {
      if (doc.visibilityState === "visible") reg.update().catch(() => {});
    };
    doc.addEventListener("visibilitychange", onVisible);
    cleanups.push(() => { reg.removeEventListener("updatefound", onFound); doc.removeEventListener("visibilitychange", onVisible); });
    return reg;
  }).catch(() => null);

  const ready = doc.readyState === "complete"
    ? start()
    : new Promise((resolve) => {
      const onLoad = () => resolve(start());
      win.addEventListener("load", onLoad, { once: true });
      cleanups.push(() => win.removeEventListener("load", onLoad));
    });
  return { ready, dispose: () => cleanups.forEach((fn) => fn()) };
}

// ─────────────────────────── censo de pestañas ──────────────────────────────
/** Responde a los censos de otras pestañas. `getDirty()` → ¿tengo trabajo sin
 *  enviar? Devuelve el desinstalador. */
export function startTabPresence({ win = window, getDirty = () => false, Channel = win.BroadcastChannel } = {}) {
  if (!Channel) return () => {};
  const ch = new Channel(CHANNEL);
  ch.onmessage = (e) => {
    const d = e && e.data;
    if (d && d.t === "who") {
      try { ch.postMessage({ t: "here", nonce: d.nonce, dirty: !!getDirty() }); } catch (_) { /* cerrado */ }
    }
  };
  return () => { try { ch.close(); } catch (_) { /* ya cerrado */ } };
}

/** Pregunta a las pestañas que ejecutan este código. Cuenta también a la que
 *  pregunta (su propio `startTabPresence` contesta). */
export function surveyTabs({ win = window, timeoutMs = 300, Channel = win.BroadcastChannel } = {}) {
  if (!Channel) return Promise.resolve({ replied: 0, dirty: 0, supported: false });
  return new Promise((resolve) => {
    const ch = new Channel(CHANNEL);
    const nonce = Math.random().toString(36).slice(2);
    let replied = 0; let dirty = 0;
    ch.onmessage = (e) => {
      const d = e && e.data;
      if (d && d.t === "here" && d.nonce === nonce) { replied += 1; if (d.dirty) dirty += 1; }
    };
    ch.postMessage({ t: "who", nonce });
    win.setTimeout(() => {
      try { ch.close(); } catch (_) { /* ya cerrado */ }
      resolve({ replied, dirty, supported: true });
    }, timeoutMs);
  });
}

export function askWorker(worker, message, { timeoutMs = 1500, win = window } = {}) {
  return new Promise((resolve) => {
    let t = null;
    try {
      const mc = new win.MessageChannel();
      t = win.setTimeout(() => resolve(null), timeoutMs);
      mc.port1.onmessage = (e) => { win.clearTimeout(t); resolve(e.data); };
      worker.postMessage(message, [mc.port2]);
    } catch (_) { if (t) win.clearTimeout(t); resolve(null); }
  });
}

/**
 * Qué se llevaría por delante activar la versión en espera.
 * `legacyTabs` = pestañas abiertas que NO ejecutan este código (versión
 * anterior): esas recargan solas al cambiar el controlador. `null` = no se pudo
 * comprobar.
 */
export async function inspectOpenTabs({ worker, win = window, timeoutMs } = {}) {
  const [survey, clients] = await Promise.all([
    surveyTabs({ win, timeoutMs }),
    worker ? askWorker(worker, { type: "COUNT_CLIENTS" }, { win }) : Promise.resolve(null),
  ]);
  const total = clients && Number.isFinite(clients.count) ? clients.count : null;
  const legacyTabs = total != null && survey.supported ? Math.max(0, total - survey.replied) : null;
  return { total, aware: survey.replied, dirtyTabs: survey.dirty, legacyTabs };
}

// ─────────────────────────────── aplicar ────────────────────────────────────
/** Pide al SW en espera que tome el control. Resuelve true cuando ESTA pestaña
 *  ve el `controllerchange`; false si no llega en `timeoutMs`. No recarga. */
export function requestActivation({ worker, nav = navigator, win = window, timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (!worker || typeof worker.postMessage !== "function") { resolve(false); return; }
    state.applying = true;
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      nav.serviceWorker.removeEventListener("controllerchange", onChange);
      win.clearTimeout(timer);
      state.applying = false;
      resolve(v);
    };
    const onChange = () => finish(true);
    nav.serviceWorker.addEventListener("controllerchange", onChange);
    const timer = win.setTimeout(() => finish(false), timeoutMs);
    try { worker.postMessage({ type: "SKIP_WAITING" }); } catch (_) { finish(false); }
  });
}
